import JSZip from "jszip";

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const g = (n) => (Number.isFinite(n) ? n.toPrecision(7) : "0");

// Facet blocks for one geometry, normals computed from triangle winding.
function facetsFor(g0) {
  const geo = g0.index ? g0.toNonIndexed() : g0;
  const p = geo.attributes.position.array;
  let out = "";
  for (let i = 0; i < p.length; i += 9) {
    const ax = p[i], ay = p[i + 1], az = p[i + 2];
    const bx = p[i + 3], by = p[i + 4], bz = p[i + 5];
    const cx = p[i + 6], cy = p[i + 7], cz = p[i + 8];
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    let nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz) || 1;
    nx /= len; ny /= len; nz /= len;
    out += `facet normal ${g(nx)} ${g(ny)} ${g(nz)}\nouter loop\n`;
    out += `vertex ${g(ax)} ${g(ay)} ${g(az)}\n`;
    out += `vertex ${g(bx)} ${g(by)} ${g(bz)}\n`;
    out += `vertex ${g(cx)} ${g(cy)} ${g(cz)}\n`;
    out += "endloop\nendfacet\n";
  }
  return out;
}

const solidStl = (name, geos) => `solid ${name}\n${geos.map(facetsFor).join("")}endsolid ${name}\n`;

// Single ASCII STL with all bodies merged (one part in the slicer).
export function exportSTL(solids, baseGeoms, filename) {
  const geos = [...solids.map((s) => s.geometry), ...(baseGeoms || [])];
  download(new Blob([solidStl("platesnap", geos)], { type: "model/stl" }), filename);
}

const safeName = (s) => (s || "part").replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "") || "part";

// One STL file per body, bundled in a ZIP so parts stay separate in the slicer.
export async function exportPerObjectSTL(solids, baseGeoms, filename) {
  const zip = new JSZip();
  const used = {};
  const add = (name, geos) => {
    let base = safeName(name);
    used[base] = (used[base] || 0) + 1;
    if (used[base] > 1) base = `${base}_${used[base]}`;
    zip.file(`${base}.stl`, solidStl(base, geos));
  };
  solids.forEach((s, i) => add(s.name || `shape_${i + 1}`, [s.geometry]));
  (baseGeoms || []).forEach((geo, i) => add(`base_${i + 1}`, [geo]));
  const blob = await zip.generateAsync({ type: "blob" });
  download(blob, filename);
}

const f = (n) => (Number.isFinite(n) ? n.toFixed(4) : "0");

function objectXml(geometry, id) {
  const geo = geometry.index ? geometry.toNonIndexed() : geometry;
  const pos = geo.attributes.position.array;
  const nv = pos.length / 3;
  let verts = "";
  for (let i = 0; i < nv; i++) {
    verts += `<vertex x="${f(pos[i * 3])}" y="${f(pos[i * 3 + 1])}" z="${f(pos[i * 3 + 2])}"/>`;
  }
  let tris = "";
  for (let i = 0; i < nv; i += 3) {
    tris += `<triangle v1="${i}" v2="${i + 1}" v3="${i + 2}"/>`;
  }
  return `<object id="${id}" type="model"><mesh><vertices>${verts}</vertices><triangles>${tris}</triangles></mesh></object>`;
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="model" ContentType="application/vnd.ms-package.3dmanufacturing-3dmodel+xml"/>
</Types>`;

const RELS = `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Target="/3D/3dmodel.model" Id="rel0" Type="http://schemas.microsoft.com/3dmanufacturing/2013/01/3dmodel"/>
</Relationships>`;

export async function export3MF(solids, baseGeoms, filename) {
  const all = [
    ...solids.map((s) => s.geometry),
    ...(baseGeoms || []),
  ];
  let objects = "";
  let build = "";
  all.forEach((g, i) => {
    objects += objectXml(g, i + 1);
    build += `<item objectid="${i + 1}"/>`;
  });
  const model = `<?xml version="1.0" encoding="UTF-8"?>
<model unit="millimeter" xml:lang="en-US" xmlns="http://schemas.microsoft.com/3dmanufacturing/core/2015/02">
<resources>${objects}</resources>
<build>${build}</build>
</model>`;
  const zip = new JSZip();
  zip.file("[Content_Types].xml", CONTENT_TYPES);
  zip.folder("_rels").file(".rels", RELS);
  zip.folder("3D").file("3dmodel.model", model);
  const blob = await zip.generateAsync({ type: "blob", mimeType: "application/vnd.ms-package.3dmanufacturing-3dmodel+xml" });
  download(blob, filename);
}
